import { composerSettings } from '@ddc-webplatform/cmsc-modules';
import { LocalePath, PageData } from 'models';

/** Pure helper – mirrors legacy AddPageWindow front-end validation rules */
export const getPathError = (
	val: string,
	formatMessage: (descriptor: { id: string; defaultMessage: string }) => string
): string | null => {
	if (!/.htm$/.test(val)) {
		return formatMessage({
			id: 'PATH_ERROR_MUST_END_IN_HTM',
			defaultMessage: 'Page path must end in .htm.'
		});
	}
	if (val.indexOf('+') > -1) {
		return formatMessage({
			id: 'PATH_ERROR_MUST_NOT_CONTAIN_PLUS',
			defaultMessage: 'The path must not contain "+".'
		});
	}
	if (!/^\//.test(val) && !/^http:/.test(val)) {
		return formatMessage({
			id: 'PATH_ERROR_BEGIN_WITH',
			defaultMessage: "Path must begin with '/' or 'http:'."
		});
	}
	return null;
};

/** Derive localePaths by iterating over composerSettings.languages (e.g. ['en_US', 'es_US', 'fr_CA']).
 *  For each language a path is taken from pageData.localePaths when available, otherwise it falls
 *  back to the base pagePath. When no languages are configured the existing localePaths are used
 *  directly, with a final fallback to a single en_US entry. */
export const getLocalePaths = (data: PageData | null | undefined): LocalePath[] => {
	const { languages } = composerSettings.getComposerSettings() as {
		languages?: string[];
	};

	// Build a locale → path lookup from the server-returned localePaths
	const entryPathMap: Record<string, string> = data?.localePaths?.length
		? data.localePaths.reduce(
				(acc, { locale, path }) => ({ ...acc, [locale]: path }),
				{} as Record<string, string>
		  )
		: {};
	const fallbackPath = data?.pagePath || '';

	if (languages?.length) {
		return [...languages]
			.sort((a, b) => a.localeCompare(b))
			.map(locale => ({ locale, path: entryPathMap[locale] ?? fallbackPath }));
	}

	// No languages configured – fall back to existing localePaths or a single en_US entry
	if (data?.localePaths?.length) {
		return [...data.localePaths].sort((a, b) =>
			a.locale.localeCompare(b.locale)
		);
	}
	return [{ locale: 'en_US', path: fallbackPath }];
};
